import React from "react";
import { motion } from "framer-motion";
import {
  FiSearch,
  FiDatabase,
  FiFilter,
  FiZap,
  FiCheck,
} from "react-icons/fi";
import { StreamPhase, PHASE_CONFIG } from "../../stream";
import styles from "./StreamingProgress.module.css";

interface StreamingProgressProps {
  /** Current progress percentage (0-100) */
  progress: number;
  /** Current processing phase */
  phase: StreamPhase | null;
  /** Optional custom message (falls back to phase default) */
  message?: string;
}

/**
 * Icon component for each phase
 */
const PhaseIcon: React.FC<{ phase: StreamPhase | null; isActive: boolean }> = ({
  phase,
  isActive,
}) => {
  const iconClass = `${styles.icon} ${isActive ? styles.iconPulse : ""}`;

  switch (phase) {
    case "preparing":
      return <FiSearch className={iconClass} data-phase={phase} />;
    case "searching":
      return <FiDatabase className={iconClass} data-phase={phase} />;
    case "reranking":
      return <FiFilter className={iconClass} data-phase={phase} />;
    case "generating":
      return <FiZap className={iconClass} data-phase={phase} />;
    case "complete":
      return <FiCheck className={iconClass} data-phase={phase} />;
    default:
      return <FiSearch className={iconClass} />;
  }
};

/**
 * Get display message for current phase
 */
const getDisplayMessage = (
  phase: StreamPhase | null,
  customMessage?: string
): string => {
  if (customMessage) {
    return customMessage;
  }
  if (phase && PHASE_CONFIG[phase]) {
    return PHASE_CONFIG[phase].message;
  }
  return "Processing...";
};

/**
 * StreamingProgress component displays a progress bar with phase-specific
 * messaging and icons during the streaming completion process.
 *
 * Features:
 * - Animated progress bar (0-100%)
 * - Phase-specific icons and messages
 * - Indeterminate animation during "preparing" phase
 * - Smooth transitions between phases
 *
 * @example
 * ```tsx
 * <StreamingProgress
 *   progress={40}
 *   phase="reranking"
 *   message="Finding the best matches..."
 * />
 * ```
 */
export const StreamingProgress: React.FC<StreamingProgressProps> = ({
  progress,
  phase,
  message,
}) => {
  const displayMessage = getDisplayMessage(phase, message);
  const isIndeterminate = phase === "preparing" && progress < 10;
  const isComplete = phase === "complete";
  const isActive = !isComplete && phase !== null;

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className={styles.progressWrapper}>
        {/* Progress bar */}
        <div
          className={styles.progressBar}
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={displayMessage}
        >
          <div
            className={styles.progressFill}
            style={{ width: `${progress}%` }}
            data-phase={phase}
            data-indeterminate={isIndeterminate}
          />
        </div>

        {/* Status row with icon, message, and percentage */}
        <div className={styles.statusRow}>
          <div className={styles.phaseInfo}>
            <PhaseIcon phase={phase} isActive={isActive} />
            <span className={isActive ? styles.messageActive : styles.message}>
              {displayMessage}
            </span>
          </div>
          <span className={styles.percentage}>{Math.round(progress)}%</span>
        </div>
      </div>
    </motion.div>
  );
};
